import React from "react";
import MeritCard from "@/components/MeritCard";
import { useI18n } from "@/i18n";
import { Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

// Heuristic "smart match": finds the member's weakest fortune dimension and
// surfaces merit projects whose title/subcategory match that need. Falls back
// to the curated pool when nothing matches. Uses real fortune indexes.
const DIMENSIONS = [
  { key: "wealth_index", zh: "财运", en: "Wealth", kw: ["财", "招财", "偏财", "聚财", "金", "wealth", "prosperity"] },
  { key: "career_index", zh: "事业学业", en: "Career", kw: ["事业", "文昌", "功名", "学业", "工作", "career", "study"] },
  { key: "love_index", zh: "姻缘感情", en: "Love", kw: ["姻缘", "桃花", "和合", "感情", "月老", "love", "relationship"] },
  { key: "health_index", zh: "健康", en: "Health", kw: ["健康", "消灾", "祻病", "平安", "延寿", "药师", "health", "healing"] },
];

function matchText(p) {
  return `${p.title_zh || ""} ${p.title_en || ""} ${p.subcategory || ""} ${p.desc_zh || ""}`.toLowerCase();
}

export const SmartMatch = ({ fortune, pool }) => {
  const { t, L } = useI18n();
  const navigate = useNavigate();
  const f = fortune || {};
  const items = Array.isArray(pool) ? pool.filter((p) => p && p.category !== "service") : [];
  if (items.length === 0) return null;

  // weakest dimension present in data
  let weak = DIMENSIONS[0];
  let min = Infinity;
  DIMENSIONS.forEach((d) => {
    const v = Number(f[d.key]);
    if (Number.isFinite(v) && v < min) { min = v; weak = d; }
  });

  const kws = weak.kw.map((k) => k.toLowerCase());
  let matches = items.filter((p) => kws.some((k) => matchText(p).includes(k)));
  const fallback = matches.length === 0;
  if (fallback) matches = items;
  matches = matches.slice(0, 2);

  const dimLabel = L(weak.zh, weak.en);

  return (
    <div data-testid="smart-match">
      <div className="flex items-center gap-2 mb-1">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[hsla(var(--brand-gold-soft),0.5)]">
          <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--brand-bronze))]" />
        </span>
        <span className="text-sm font-bold text-foreground">{L("智能配对推荐", "Smart Match")}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        {fallback
          ? L("为你精选的功德项目", "Hand-picked merit projects for you")
          : L(`依你今日【${dimLabel}】偏弱，建议加持`, `Boost your ${dimLabel.toLowerCase()} today`)}
      </p>
      <div className="space-y-4">
        {matches.map((p) => <MeritCard key={p.id} project={p} />)}
      </div>
      <Button variant="outline" className="w-full mt-3 border-[hsl(var(--brand-gold))]" onClick={() => navigate("/daily")} data-testid="smart-match-viewall">
        {t("common.viewAll")} <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
};
